// src/pages/providers/2_dashboard/components/QuickFilterChips.js
//
// Quick-filter chip toolbar for the patient dashboard.
//
// Renders toggle-style chips — each with a live count badge showing how many
// non-archived patients match that filter across all pages. Journey chips
// follow the assessment path chronologically (login → clinician → forms →
// report); some orgs also see orthogonal ops chips (e.g. Well Life Clinic's
// "Needs MDT clinician") that may overlap journey stages in meaning.
// Designed for ops staff who need to rapidly triage patients that require
// action, multiple times per day.
//
// UI is single-select: activating one chip deactivates any other. Clicking
// the already-active chip toggles it off (returns to default view).
//
// The parent restricts which chips are shown via `visibleChipKeys` (from
// getVisibleChipKeys). Examples: "Needs clinician assigned" is hidden for
// orgs that require a lead at setup; "Needs MDT clinician" is Well Life
// Clinic only.
//
// Count computation is O(n) over the full patient list but runs inside a
// useMemo keyed on `patients` — negligible for the hundreds-to-low-thousands
// scale of a single organisation's summaries.

import React, { useMemo } from 'react';
import { CHIP_FILTERS, CHIP_FILTER_ORDER } from '../../../../utils/patientFilterUtils';

function QuickFilterChips({ patients, activeChip, onChipChange, visibleChipKeys }) {
  // Fall back to the full ordered list if the parent doesn't restrict visibility.
  const chipKeys = visibleChipKeys || CHIP_FILTER_ORDER;

  // Compute counts for each visible chip from the full non-archived patient list.
  // Archived patients are excluded because the chip toolbar only appears
  // above the "Current Patients" table which already filters them out.
  const counts = useMemo(() => {
    const nonArchived = (patients || []).filter(p => p.isArchived !== true);

    return chipKeys.reduce((acc, key) => {
      acc[key] = nonArchived.filter(CHIP_FILTERS[key].predicate).length;
      return acc;
    }, {});
  }, [patients, chipKeys]);

  const handleClick = (chipKey) => {
    // Toggle off if clicking the already-active chip, otherwise activate the new one
    onChipChange(activeChip === chipKey ? null : chipKey);
  };

  return (
    <div
      role="toolbar"
      aria-label="Quick filters"
      className="flex flex-wrap items-center gap-2"
    >
      <span className="text-xs font-medium text-gray-500 mr-1">Quick filters:</span>

      {chipKeys.map((chipKey) => {
        const chip = CHIP_FILTERS[chipKey];
        const count = counts[chipKey] || 0;
        const isActive = activeChip === chipKey;

        return (
          <button
            key={chipKey}
            type="button"
            onClick={() => handleClick(chipKey)}
            aria-pressed={isActive}
            aria-label={`${chip.label}, ${count} patient${count !== 1 ? 's' : ''}`}
            className={`
              relative inline-flex items-center gap-1.5 px-3 py-1.5
              text-xs font-medium rounded-full border
              cursor-pointer select-none
              transition-[background-color,color,border-color,box-shadow,transform] duration-300 ease-in-out
              active:scale-95
              ${isActive
                ? 'bg-primary text-white border-primary shadow-md ring-2 ring-primary/30'
                : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200 hover:border-gray-300 shadow-none ring-0'
              }
            `}
          >
            {chip.label}

            {/* Count badge — uses brand secondary (gold) when inactive with results,
                matching the brand colour scheme from tailwind.config.js */}
            <span
              aria-hidden="true"
              className={`
                inline-flex items-center justify-center
                min-w-[20px] h-5 px-1.5
                text-[11px] font-semibold rounded-full
                transition-[background-color,color] duration-300 ease-in-out
                ${isActive
                  ? 'bg-white/25 text-white'
                  : count > 0
                    ? 'bg-primary text-white'
                    : 'bg-gray-300 text-gray-600'
                }
              `}
            >
              {count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default QuickFilterChips;
